import React, { Fragment, forwardRef, useState, useEffect, useImperativeHandle } from 'react';
import dayjs from 'dayjs';
import ScheduledCallbackBody from "./ScheduledCallbackBody";
import SvgIcon from "../../images/SvgIcon";
import { fetch, getOrdinalIndicator } from "../../../modules/utils";
import { trackEvent } from "../../../modules/tracking";
import { AnalyticsEvents, resolveTrackingEventKey } from "../../../modules/analyticsEvents";
const steps = { intro: 0, form: 1, confirmation: 2 };
const createInitialState = () => ({ currentStep: steps.intro, callbackPreference: 'Today', otherDaySelectedValue: '', timeSelectedValue: '', nextOpenDayString: '', nextOpenTimeString: '', callbackTimeOptions: [], otherDayOptions: [], callbackScheduled: false, selectedDayIndex: 0, selectedDayAsString: '', submissionErrorText: '' });
const formattableDateUTC = date => dayjs.utc(date);
const createTimeRangeString = (from, to) => { const fromTime = formattableDateUTC(from).format('HH:mm'); const toTime = formattableDateUTC(to).format('HH:mm'); return `${fromTime} - ${toTime}`; };
const createTimeString = time => formattableDateUTC(time).format('HH:mm');
const createDayString = date => formattableDateUTC(date).format('dddd');
const createDayWithOridinalString = date => { const dateStr = createDayString(date); const dayStr = formattableDateUTC(date).format('D'); const ordinalStr = getOrdinalIndicator(parseInt(dayStr, 10)); return `${dateStr} ${dayStr}${ordinalStr}`; }; // Component here
export const ScheduledCallback = /*#__PURE__*/ forwardRef(function ScheduledCallback({ openingData, callCentreWillOpenToday, todaysDay, stepsContent }, ref) {
    const [state, setState] = useState(createInitialState);
    useImperativeHandle(ref, () => ({ init() { setState(createInitialState()); initCallbackPreference(); initOtherDayOptions(); } }));
    const setSelectedDayAsString = selectedDayIndex => { const newSelectedDayString = createDayWithOridinalString(openingData[selectedDayIndex].date); setState(prev => ({ ...prev, selectedDayAsString: newSelectedDayString })); };
    const setCallbackTimeOptions = newSelectedDayIndex => {
        const openingTimesToday = openingData[newSelectedDayIndex].openingTimes;
        const newNextOpenTimeString = createTimeString(openingTimesToday[0].from); // Filter to skip the first opening slot (to enable the ops team to prepare each morning)
        const newCallbackTimeOptions = openingTimesToday.filter((times, index) => index !== 0).map(times => createTimeRangeString(times.from, times.to));
        const newTimeSelectedValue = newCallbackTimeOptions[0];
        setState(prev => ({ ...prev, callbackTimeOptions: newCallbackTimeOptions, timeSelectedValue: newTimeSelectedValue, nextOpenTimeString: newNextOpenTimeString }));
    };
    const initCallbackPreference = () => {
        let newCallbackPreference = '';
        let newNextOpenDayString = '';
        let newSelectedDayIndex = 0; // If open today, preference is today
        if (callCentreWillOpenToday) {
            newCallbackPreference = 'Today';
            newNextOpenDayString = 'today';
        }
        else if (todaysDay === 'Saturday') {
            newCallbackPreference = 'Other';
            newSelectedDayIndex = 1;
            newNextOpenDayString = 'Monday';
        }
        else if (todaysDay === 'Sunday') {
            newCallbackPreference = 'Tomorrow';
            newSelectedDayIndex = 0;
            newNextOpenDayString = 'Monday';
        }
        else {
            newCallbackPreference = 'Tomorrow';
            newNextOpenDayString = 'tomorrow';
            newSelectedDayIndex = 1;
        }
        setSelectedDayAsString(newSelectedDayIndex);
        setCallbackTimeOptions(newSelectedDayIndex);
        setState(prev => ({ ...prev, callbackPreference: newCallbackPreference, nextOpenDayString: newNextOpenDayString, selectedDayIndex: newSelectedDayIndex }));
    };
    const initOtherDayOptions = () => {
        let sliceIndex = 2;
        if (todaysDay === 'Saturday' || todaysDay === 'Sunday') {
            sliceIndex = 1;
        } // Map 7 days after 'tomorrow' by slicing since this is for 'other'
        const days = openingData.slice(sliceIndex, openingData.length).map((day, index) => {
            return [`${createDayString(day.date)}#${index + sliceIndex}`, // Key/Value
                createDayWithOridinalString(day.date) // Label
            ];
        });
        setState(prev => ({ ...prev, otherDayOptions: days }));
    };
    const init = () => { setState(createInitialState()); initCallbackPreference(); initOtherDayOptions(); };
    useEffect(() => { init(); }, []);
    const nextStep = () => { setState(prev => ({ ...prev, currentStep: prev.currentStep + 1 })); };
    const showError = () => { setState(prev => ({ ...prev, submissionErrorText: 'There was an error, please try again.' })); };
    const hideError = () => { setState(prev => ({ ...prev, submissionErrorText: '' })); };
    const callAPI = (method, data) => { return new Promise((resolve, reject) => { fetch(method, `/scheduled_callbacks.json`, 'application/json', null, data).then(data => resolve(data)).catch(errResponse => reject(errResponse)); }); };
    const scheduleCallback = (method, payload) => { callAPI(method, payload).then(data => { if (data.created_at) {
        nextStep();
    }
    else {
        showError();
    } }).catch(() => { showError(); }); };
    const handleSubmit = () => { const date = openingData[state.selectedDayIndex].date; const time = state.timeSelectedValue.split('-')[0].trim(); const payload = { scheduled_at: `${date}T${time}Z` }; hideError(); scheduleCallback('POST', payload); };
    const onCallbackDayChange = e => { switch (e.currentTarget.name) {
        case 'callbackDay':
            const newSelectedDayIndex = parseInt(e.currentTarget.value.split('#')[1]);
            const newOtherDaySelectedValue = `${createDayString(openingData[newSelectedDayIndex].date)}#${newSelectedDayIndex}`;
            setSelectedDayAsString(newSelectedDayIndex);
            setCallbackTimeOptions(newSelectedDayIndex);
            setState(prev => ({ ...prev, otherDaySelectedValue: newOtherDaySelectedValue, selectedDayIndex: newSelectedDayIndex }));
            break;
        case 'callbackTime':
            setState(prev => ({ ...prev, timeSelectedValue: e.currentTarget.value }));
            break;
    } };
    const onCallbackPreferenceChange = e => { let newSelectedDayIndex = 0; switch (e.callbackPreference.toLowerCase()) {
        case 'today': break;
        case 'tomorrow':
            if (todaysDay === 'Sunday') {
                newSelectedDayIndex = 0;
            }
            else {
                newSelectedDayIndex = 1;
            }
            break;
        case 'other': if (todaysDay === 'Saturday' || todaysDay === 'Sunday') {
            newSelectedDayIndex = 1;
        }
        else {
            newSelectedDayIndex = 2;
        }
    } const newOtherDaySelectedValue = `${createDayString(openingData[newSelectedDayIndex].date)}#${newSelectedDayIndex}`; setSelectedDayAsString(newSelectedDayIndex); setCallbackTimeOptions(newSelectedDayIndex); setState(prev => ({ ...prev, callbackPreference: e.callbackPreference, selectedDayIndex: newSelectedDayIndex, otherDaySelectedValue: newOtherDaySelectedValue })); };
    const handleDateChange = e => { hideError(); if (e.currentTarget) {
        onCallbackDayChange(e);
    }
    else {
        onCallbackPreferenceChange(e);
    } };
    const onClickNext = e => { e.preventDefault(); if (state.currentStep < steps.form) {
        nextStep();
        trackEvent(AnalyticsEvents.help.callbackContactChosen());
    }
    else {
        setSelectedDayAsString(state.selectedDayIndex);
        handleSubmit();
    } };
    const renderHeader = () => { const { icon, title } = stepsContent[state.currentStep]; return /*#__PURE__*/ React.createElement("header", { className: "scheduled-callback__header" }, /*#__PURE__*/ React.createElement("div", { className: "scheduled-callback__icon" }, icon && /*#__PURE__*/ React.createElement(SvgIcon, { name: icon, alt: icon })), /*#__PURE__*/ React.createElement("h2", { className: "scheduled-callback__title" }, title)); };
    const renderFooter = () => {
        let { linkTarget } = stepsContent[state.currentStep];
        const { linkText, linkHref, buttonText, trackingEvent } = stepsContent[state.currentStep];
        const target = linkTarget ? linkTarget = linkTarget : '';
        const onLinkClick = e => {
            e.preventDefault(); // `trackingEvent` is a server-rendered KEY (from the Rails view config,
            // e.g. _scheduled_callback_modal.html.erb) resolved against the
            // AnalyticsEvents catalog below, rather than a raw event name string -
            // this keeps every value passed to trackEvent() catalog-sourced.
            const resolvedEvent = resolveTrackingEventKey(trackingEvent);
            if (resolvedEvent) {
                trackEvent(resolvedEvent).then(() => {
                    if (target) {
                        window.open(linkHref, target);
                        return;
                    }
                    window.location = linkHref;
                });
            }
        };
        return /*#__PURE__*/ React.createElement("footer", { className: "scheduled-callback__footer" }, state.submissionErrorText !== '' && /*#__PURE__*/ React.createElement("p", { className: "scheduled-callback__error" }, state.submissionErrorText), buttonText !== '' && /*#__PURE__*/ React.createElement("button", { onClick: onClickNext, type: "submit", value: "Submit", className: "button--primary button--block button" }, buttonText), /*#__PURE__*/ React.createElement("a", { href: linkHref, target: target, onClick: onLinkClick, className: "link--sm link" }, linkText));
    };
    return /*#__PURE__*/ React.createElement(Fragment, null, /*#__PURE__*/ React.createElement("div", { className: "scheduled-callback" }, renderHeader(), /*#__PURE__*/ React.createElement("div", { className: "scheduled-callback__body" }, /*#__PURE__*/ React.createElement(ScheduledCallbackBody, { steps: steps, todaysDay: todaysDay, callCentreWillOpenToday: callCentreWillOpenToday, callbackPreference: state.callbackPreference, otherDaySelectedValue: state.otherDaySelectedValue, selectedDayAsString: state.selectedDayAsString, timeSelectedValue: state.timeSelectedValue, callbackTimeOptions: state.callbackTimeOptions, otherDayOptions: state.otherDayOptions, currentStep: state.currentStep, nextOpenDayString: state.nextOpenDayString, nextOpenTimeString: state.nextOpenTimeString, handleDateChange: handleDateChange })), renderFooter()));
});
